// src/components/about/About.tsx

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Divider,
    Button,
    Chip,
    Avatar,
    useTheme,
    useMediaQuery,
    Link,
} from '@mui/material';
import {
    Info as InfoIcon,
    Code as CodeIcon,
    Telegram as TelegramIcon,
    Image as ImageIcon,
    Policy as PolicyIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';

const About: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useAuth();

    const [policyDialogOpen, setPolicyDialogOpen] = useState(false);

    return (
        <Box>
            {/* Заголовок */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    component="h1"
                    sx={{ fontWeight: 500 }}
                >
                    ℹ️ О приложении
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Информация о платформе
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Основная информация */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                <InfoIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                О приложении
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Название
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    rusyaikina-tutor
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Версия
                                </Typography>
                                <Typography variant="body1">1.0.0</Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Платформа
                                </Typography>
                                <Typography variant="body1">Tutor Platform</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Разработчик и иконка */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                                <CodeIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Разработчик
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Telegram
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TelegramIcon fontSize="small" color="action" />
                                    <Link
                                        href="https://t.me/greatstep"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        @greatstep
                                    </Link>
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Иконка приложения
                                </Typography>
                                <Typography variant="body2">
                                    <Link
                                        href="https://www.flaticon.com/ru/free-icons/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ color: 'inherit', textDecoration: 'underline' }}
                                    >
                                        juicy_fish
                                    </Link>
                                    {' '}— Flaticon
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Политика конфиденциальности */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                                <LockIcon />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Правовая информация
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 },
                        }}>
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Политика конфиденциальности
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Как мы обрабатываем и защищаем ваши персональные данные
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<PolicyIcon />}
                                onClick={() => setPolicyDialogOpen(true)}
                            >
                                Открыть
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Диалог политики конфиденциальности */}
            <PrivacyPolicyDialog
                open={policyDialogOpen}
                onClose={() => setPolicyDialogOpen(false)}
            />
        </Box>
    );
};

export default About;